import { Box, Typography } from "@mui/material";
import { TaxNumberComponent } from "@soqs/common";
import { useSelector } from "react-redux";
import SelectComponent from "../../FormComponents/DefaultSelect/SelectComponent";
import IDSelectionComponent from "./IDSelection";
import {
  dropdownOptions,
  requesterDetailsFormState,
} from "../../../features/CaptureDetailsSlice";
import {
  EntityTypes,
  CaptureDetailsPrefixConstants,
} from "../../../models/Enums";
import MenuItemsForDropdown from "../../FormComponents/MenuItemsForDropdown/MenuItemsForDropdown";
import InputFieldComponent from "../../FormComponents/InputField/InputField";
import {
  getIDDisplay,
  invalidRepresentative,
  invalidTaxPractitioner,
} from "../../../features/Helper";

export interface TaxpayerDetailsProps {
  entityArray: string;
}

const TaxpayerDetails = (props: TaxpayerDetailsProps) => {
  const selectOptions = useSelector(dropdownOptions);
  const captureDetailsForm = useSelector(requesterDetailsFormState);

  const taxPractitionerIDTypeDisplay = getIDDisplay(captureDetailsForm?.values?.taxPractInfo_idType);
  const representativeIDTypeDisplay = getIDDisplay(captureDetailsForm?.values?.registeredRepresentInfo_idType);

  const trustRegNoRequired = captureDetailsForm?.values?.trustType?.toLowerCase() !== 'Collective Investment Schemes in Securities'.toLowerCase() && captureDetailsForm?.values?.trustType?.toLowerCase() !== 'Foreign Trust (Non-Resident Trust)'.toLowerCase();

  return (
    <>
      <Typography
        sx={(theme) => ({
          fontSize: theme.typography.h5,
          mt: "22px",
          pb: "8px",
        })}
      >
        Taxpayer Details
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={(theme) => ({
            flex: "0 0 49%",
            [theme.breakpoints.down("sm")]: {
              flex: "0 0 100%",
            },
          })}
        >
          <SelectComponent
            id="entity-type"
            label="Entity Type"
            name="entityType"
            required={true}
            options={MenuItemsForDropdown(props.entityArray, selectOptions)}
          />
        </Box>

        {captureDetailsForm?.values["entityType"] === EntityTypes.SMME ? (
          <Box
            sx={(theme) => ({
              flex: "0 0 49%",
              [theme.breakpoints.down("sm")]: {
                flex: "0 0 100%",
              },
            })}
          >
            <SelectComponent
              id="smme-type"
              label="SMME Type"
              name="smmeType"
              required={true}
              options={MenuItemsForDropdown("smmeType", selectOptions)}
            />
          </Box>
        ) : null}
        {captureDetailsForm?.values["entityType"] === EntityTypes.Company ||
        captureDetailsForm?.values["smmeType"] === "company" ? (
          <Box
            sx={(theme) => ({
              flex: "0 0 49%",
              [theme.breakpoints.down("sm")]: {
                flex: "0 0 100%",
              },
            })}
          >
            <InputFieldComponent
              id={"company-registration-number"}
              label={"Company Registration Number"}
              name={"companyRegNo"}
              required={true}
              maxLength={14}
              minLength={14}
              regex="^[ -;=-~]{1,14}$"
            />
          </Box>
        ) : null}

        {captureDetailsForm?.values["entityType"] === EntityTypes.Trust ? (
          <>
            <Box
              sx={(theme) => ({
                flex: "0 0 49%",
                [theme.breakpoints.down("sm")]: {
                  flex: "0 0 100%",
                },
              })}
            >
              <SelectComponent
                id="trust-type"
                label="Trust Type"
                name="trustType"
                required={true}
                options={MenuItemsForDropdown("trustType", selectOptions)}
              />
            </Box>

            <Box
              sx={(theme) => ({
                flex: "0 0 49%",
                [theme.breakpoints.down("sm")]: {
                  flex: "0 0 100%",
                },
              })}
            >
              <InputFieldComponent
                id="trust-registration-number"
                label="Trust Registration Number"
                name="trustRegNo"
                required={trustRegNoRequired}
                maxLength={18}
                regex="^[A-Za-z0-9 /-]{5,18}$"
              />
            </Box>

            {captureDetailsForm?.values["entityType"] === EntityTypes.Trust ? (
              <Box
                sx={(theme) => ({
                  flex: "0 0 49%",
                  [theme.breakpoints.down("sm")]: {
                    flex: "0 0 100%",
                  },
                })}
              >
                <TaxNumberComponent id="tax-number" name="taxNumber" parse={(value: any) => value ? value?.trim() : ""} />
              </Box>
            ) : null}
          </>
        ) : null}
      </Box>

      {captureDetailsForm?.values["entityType"] !== EntityTypes.Company &&
      captureDetailsForm?.values["entityType"] !== EntityTypes.Trust &&
      captureDetailsForm?.values["smmeType"] !== "company" ? (
        <IDSelectionComponent prefix={CaptureDetailsPrefixConstants.Taxpayer} />
      ) : null}

      {invalidTaxPractitioner(captureDetailsForm) ? (
        <Typography
          sx={{ color: "#d32f2f", fontSize: "0.75rem", p: "8px 12px" }}
        >
          The Tax Practitioner {taxPractitionerIDTypeDisplay} provided is the same as the Taxpayer {taxPractitionerIDTypeDisplay}. Please select the Requestor Type as Individual if you are making a booking for yourself.
        </Typography>
      ) : null}

      {invalidRepresentative(captureDetailsForm) ? (
        <Typography
          sx={{ color: "#d32f2f", fontSize: "0.75rem", p: "8px 12px" }}
        >
          The Representative {representativeIDTypeDisplay} provided is the same as the Taxpayer {representativeIDTypeDisplay}. Please select the Requestor Type as Individual if you are making a booking for yourself.
        </Typography>
      ) : null}
    </>
  );
};

export default TaxpayerDetails;
